import classNames from "classnames";
import { ReactNode, useEffect, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useWindowDimensions } from "../../customHooks/useWindowDimensions";
import {
  SKELETON_BASE_COLOR,
  SKELETON_HIGHLIGHT_COLOR,
} from "../../utils/constants";
import { ButtonVariations } from "../buttons/button";
import { ContainerCard } from "../cards/containerCard";
import { MorePopOverMenu, Option } from "../dropdown/moreMenuPopover";
import { Checkbox } from "../inputs/checkbox";
import Pagination from "../pagination/pagination";
import { EmptyState } from "../states/emptyState";

type ActionProps = {
  hasActions?: boolean;
  hasCheckbox?: boolean;
  checked?: boolean;
  onCheck?: () => void;
  onUncheck?: () => void;
  hasMoreMenu?: boolean;
  moreOptions?: Option[];
};

export const TableComponent = ({
  headers = [],
  headerActionProps,
  rows = [],
  className = "",
  isRowClickable = false,
  rowClassName = "",
  headerClassName,
  onRowClick = () => {},
  isTableEmpty = false,
  emptyTitle = "",
  emptySubTitle = "",
  emptyActionText = "",
  emptyActionOnclick = () => {},
  emptyActionVariant = "secondary",
  emptrySrc = "/assets/images/empty_state_cooperatives.svg",
  isLoading = false,
  isPaginated = false,
  currentPage = 1,
  totalCount = 0,
  itemsPerPage = 20,
  onPageChange = () => {},
  loadingHeight = 16,
  custLoader,
  stickyLastCol = false,
  mobileCardView = false,
}: {
  headers: ReactNode[];
  headerActionProps?: ActionProps;
  rows: {
    id: string | number;
    items: ReactNode[];
    onClick?: () => void;
    actionProps?: ActionProps;
  }[];
  className?: string;
  isRowClickable?: boolean;
  rowClassName?: string;
  headerClassName?: string;
  onRowClick?: (idx: number) => void;
  isTableEmpty?: boolean;
  emptyTitle?: string;
  emptySubTitle?: string;
  emptyActionText?: string;
  emptyActionOnclick?: () => void;
  emptyActionVariant?: ButtonVariations;
  emptrySrc?: string;
  isLoading?: boolean;
  isPaginated?: boolean;
  currentPage?: number;
  totalCount?: number;
  itemsPerPage?: number;
  onPageChange?: (page: number) => void;
  loadingHeight?: number;
  custLoader?: ReactNode;
  stickyLastCol?: boolean;
  mobileCardView?: boolean;
}) => {
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const [isScrolledToEnd, setIsScrolledToEnd] = useState(false);

  const moreMenuRef = useRef<HTMLButtonElement | null>(null);

  const { width } = useWindowDimensions();

  const thClassName = `
  uppercase text-[10px] font-medium
  text-pry-dark-green-300/80 bg-light-green-bg/[80%] backdrop-blur-sm
  pb-2.5 px-4  first:pl-4 md:first:pl-6  last:pr-4 md:last:pr-6
  text-left w-fit min-w-[50px] whitespace-nowrap
  ${
    stickyLastCol
      ? `sticky-table-col ${
          !isScrolledToEnd && !isLoading && width < 768
            ? "last:!border-transparent last:!rounded-none last:!pr-0"
            : ""
        }`
      : ""
  }
  ${headerClassName}
  `;

  const tbodyTrClassName = `
  group rounded-lg
  ${isRowClickable && "cursor-pointer transition-all duration-300"}
`;

  const tbodyTdClassName = `
  mb-2 duration-300 transition-all
  border-pry-dark-green-300/10 bg-white/50 backdrop-blur-sm
  ${isRowClickable && "md:group-hover:border-pry-dark-green-300/50"}
  border-t border-b ${
    mobileCardView ? "py-6" : "py-[16px]"
  } md:py-[20px] px-3 md:px-4
  first:border-l first:rounded-s-lg first:pl-4 md:first:pl-6
  last:border-r last:rounded-e-lg last:pr-4 md:last:pr-6
  text-left text-xs font-medium text-pry-dark-green-400/80
  ${
    stickyLastCol
      ? `sticky-table-col ${
          !isScrolledToEnd && !isLoading && width < 768
            ? "last:!border-l last:border-l-pry-dark-green-300/[3%] last:!border-r-0- last:!rounded-e-none last:!pr-0"
            : ""
        }`
      : ""
  }
  ${rowClassName}
  `;

  useEffect(() => {
    const wrapper = tableWrapperRef.current;
    if (!wrapper) return;

    const handleScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = wrapper;
      const atEnd = scrollLeft + clientWidth >= scrollWidth - 45;
      setIsScrolledToEnd(atEnd);
    };

    if (!isLoading) {
      handleScroll();
    }

    wrapper.addEventListener("scroll", handleScroll);

    return () => {
      wrapper.removeEventListener("scroll", handleScroll);
    };
  }, [isLoading]);

  return (
    <div ref={tableWrapperRef} className="relative overflow-x-scroll">
      <table
        className={classNames(
          `
          min-w-full md:min-w-full
          border-separate border-spacing-x-0 border-spacing-y-1
          ${className}
          `,
          {
            "max-md:border-spacing-y-3": mobileCardView,
          }
        )}
      >
        {isTableEmpty && !isLoading ? (
          <tbody>
            <tr>
              <td>
                <ContainerCard className="!py-12">
                  <EmptyState
                    title={emptyTitle}
                    subtitle={emptySubTitle}
                    titleClassName=""
                    image={<img src={emptrySrc} alt="empty" />}
                    actionText={emptyActionText}
                    onClick={emptyActionOnclick}
                    actionButtonVariant={emptyActionVariant}
                  />
                </ContainerCard>
              </td>
            </tr>
          </tbody>
        ) : undefined}

        {!isTableEmpty &&
        !isLoading &&
        headers.length > 0 &&
        !(mobileCardView && width < 768) ? (
          <thead>
            <tr>
              {[
                headerActionProps?.hasCheckbox ? (
                  <Checkbox
                    checked={headerActionProps?.checked}
                    onCheck={headerActionProps?.onCheck}
                    onUnCheck={headerActionProps?.onUncheck}
                    className=""
                  />
                ) : undefined,
                ...headers,
                headerActionProps?.hasMoreMenu ? " " : undefined,
              ]
                ?.filter((i) => i !== undefined)
                ?.map((header, idx) => {
                  const key = `table-header-${idx}`;
                  return header ? (
                    <th key={key} className={thClassName}>
                      {header}
                    </th>
                  ) : undefined;
                })}
            </tr>
          </thead>
        ) : undefined}

        {isLoading && !custLoader ? (
          <tbody className="">
            {Array.from({ length: itemsPerPage }).map((_, idx) => {
              return (
                <tr key={idx} className={tbodyTrClassName}>
                  {(width < 768 || headers.length === 0 ? [1] : headers)?.map(
                    (header, idx) => {
                      const key = `table-header-${idx}`;
                      return header ? (
                        <td key={key} className={tbodyTdClassName}>
                          <Skeleton
                            height={loadingHeight}
                            baseColor={SKELETON_BASE_COLOR}
                            highlightColor={SKELETON_HIGHLIGHT_COLOR}
                            className={classNames("rounded-lg", {
                              "my-1.5 first:mt-0 last:mb-0":
                                mobileCardView && width <= 768,
                            })}
                            containerClassName="flex-1"
                            count={mobileCardView && width <= 768 ? 3 : 1}
                          />
                        </td>
                      ) : undefined;
                    }
                  )}
                </tr>
              );
            })}
          </tbody>
        ) : undefined}

        {isLoading && !!custLoader ? (
          <tbody>
            <tr>
              <td className="">{custLoader}</td>
            </tr>
          </tbody>
        ) : undefined}

        {!isLoading && !isTableEmpty ? (
          <tbody>
            {rows
              ?.filter((i) => i !== undefined)
              ?.map((row, idx) => {
                return (
                  <tr
                    key={row?.id}
                    className={tbodyTrClassName}
                    onClick={() => {
                      isRowClickable && onRowClick(idx);
                      if (row.onClick && isRowClickable) {
                        row.onClick();
                      }
                      if (
                        width < 768 &&
                        row.actionProps?.hasMoreMenu &&
                        !row?.actionProps?.hasCheckbox &&
                        !row.onClick
                      ) {
                        moreMenuRef?.current?.click();
                      }
                    }}
                  >
                    {mobileCardView && width < 768 ? (
                      <td
                        className={classNames(
                          `${tbodyTdClassName} !py-0 !px-0`,
                          {
                            "!border-pry-dark-green-300 !border":
                              row?.actionProps?.checked,
                          }
                        )}
                      >
                        <div className="flex flex-col">
                          {row?.actionProps?.hasActions ? (
                            <div
                              className={classNames(
                                "flex items-center gap-2 justify-between px-4 py-3 border-b",
                                {
                                  "hiddenp-":
                                    row?.actionProps?.hasMoreMenu &&
                                    !row?.actionProps?.hasCheckbox,
                                }
                              )}
                            >
                              {row?.actionProps?.hasCheckbox ? (
                                <Checkbox
                                  checked={row?.actionProps?.checked}
                                  onCheck={row?.actionProps?.onCheck}
                                  onUnCheck={row?.actionProps?.onUncheck}
                                  iconDim={16}
                                  className=""
                                />
                              ) : undefined}

                              {row?.actionProps?.hasMoreMenu ? (
                                <div className="ml-auto rotate-90-">
                                  <MorePopOverMenu
                                    options={row?.actionProps?.moreOptions}
                                    ref={moreMenuRef}
                                  />
                                </div>
                              ) : undefined}
                            </div>
                          ) : undefined}

                          {row?.items?.map((item, idx) => {
                            const key = `row-item-${idx}`;
                            return item ? (
                              <div
                                key={key}
                                className={`flex text-[13px] font-medium items-center gap-2 justify-between px-4 py-2 border-b border-pry-dark-green-400/5 last:border-b-0`}
                              >
                                <div className="text-pry-dark-green-300/60">
                                  {headers[idx]}:
                                </div>
                                <div className="text-pry-dark-green-300/80">
                                  {item}
                                </div>
                              </div>
                            ) : undefined;
                          })}
                        </div>
                      </td>
                    ) : (
                      <>
                        {[
                          row?.actionProps?.hasCheckbox ? (
                            <Checkbox
                              checked={row?.actionProps?.checked}
                              onCheck={row?.actionProps?.onCheck}
                              onUnCheck={row?.actionProps?.onUncheck}
                              iconDim={16}
                              className=""
                            />
                          ) : undefined,
                          ...(row?.items ?? []),
                          row?.actionProps?.hasMoreMenu ? (
                            <div className="ml-auto">
                              <MorePopOverMenu
                                options={row?.actionProps?.moreOptions}
                                ref={moreMenuRef}
                              />
                            </div>
                          ) : undefined,
                        ]
                          ?.filter((i) => i !== undefined)
                          ?.map((item, idx) => {
                            const key = `row-item-${idx}`;
                            return item ? (
                              <td key={key} className={`${tbodyTdClassName}`}>
                                {item}
                              </td>
                            ) : undefined;
                          })}
                      </>
                    )}
                  </tr>
                );
              })}
          </tbody>
        ) : undefined}
      </table>

      {isPaginated && totalCount > itemsPerPage ? (
        <div className="sticky bottom-0 left-0">
          <Pagination
            currentPage={currentPage}
            totalCount={totalCount}
            itemsPerPage={itemsPerPage}
            onPageChange={onPageChange}
            pageRangeDisplayed={4}
            containerClassName="mt-3"
            showPagNumbers={width >= 768 ? true : false}
          />
        </div>
      ) : undefined}
    </div>
  );
};
